import { computed, ref } from 'vue'
import useApiRequest from '@core/hooks/useApiRequest'
import DiagnosticsItemState from '@diagnostics/models/DiagnosticsItemState'
import {
  fetchDiagnosticsItemState,
  finishDiagnosticsItemTest,
  retryDiagnosticsItemTest
} from '@diagnostics/services/diagnostics'
import useEventEmitter from '@core/hooks/useEventEmitter'
import type { DiagnosticsItemStateRemoteData } from '@diagnostics/services/types'

interface Events {
  'update-state-success': never
  'request-submit-attempt-success': never
  'retry-test-success': unknown
}

const DiagnosticsItemStateController = () => {
  const { $on, $off, $emit } = useEventEmitter<Events>()

  // Data
  const data = ref<DiagnosticsItemState | null>(null)
  const setData = (responseData: DiagnosticsItemStateRemoteData | null) => {
    data.value = responseData ? new DiagnosticsItemState(responseData) : null
    $emit('update-state-success')
  }

  const itemRequest = useApiRequest(fetchDiagnosticsItemState)

  itemRequest.$on('success', setData)

  const request = async (id: number) => {
    await itemRequest.request(id)
  }

  // Submit diagnostics.
  const submitDiagnosticsRequest = useApiRequest(finishDiagnosticsItemTest)

  submitDiagnosticsRequest.$on('success', responseData => {
    setData(responseData)
    $emit('request-submit-attempt-success')
  })

  const submitAttempt = async (diagnosticsItemId: number) => {
    await submitDiagnosticsRequest.request(diagnosticsItemId)
  }

  // Retry
  const retryRequest = useApiRequest(retryDiagnosticsItemTest)

  const retry = (id: number) => {
    retryRequest.request(id)
  }

  retryRequest.$on('success', responseData => {
    setData(responseData)
    $emit('retry-test-success')
  })

  return {
    // fetch state
    data: computed(() => data.value),
    setData,
    isLoading: computed(() => itemRequest.isLoading),
    request,
    // submit attempt
    submitAttempt,
    isSubmittingAttempt: computed(() => submitDiagnosticsRequest.isLoading),
    isFulfilledAttempt: computed(() => submitDiagnosticsRequest.isFullfilled),

    // retry
    retry,
    isRetrying: computed(() => retryRequest.isLoading),
    $on,
    $off
  }
}

export default DiagnosticsItemStateController
